import {
  createBaseThemeOptions,
  createUnifiedTheme,
  palettes,
  UnifiedThemeOptions,
} from '@backstage/theme';
import {
  black,
  blue,
  gray,
  green,
  purple,
  red,
  white,
  yellow,
} from '@carbon/colors';
import { body01, fontFamilies } from '@carbon/type';

/**
 * IBM Dark Theme - Clean Implementation
 * Uses theme tokens and Backstage's theme system properly
 */
export const ibmDarkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      mode: 'dark',

      primary: {
        main: blue[40],
        light: blue[30],
        dark: blue[50],
        contrastText: gray[100],
      },
      secondary: {
        main: purple[50],
        light: purple[40],
        dark: purple[60],
        contrastText: gray[100],
      },
      error: {
        main: red[40],
        light: red[30],
        dark: red[50],
      },
      warning: {
        main: yellow[30],
        light: yellow[20],
        dark: yellow[30],
      },
      info: {
        main: blue[40],
        light: blue[30],
        dark: blue[50],
      },
      success: {
        main: green[50],
        light: green[40],
        dark: green[60],
      },
      background: {
        default: gray[100],
        paper: gray[90],
      },
      text: {
        primary: gray[10],
        secondary: gray[30],
        disabled: gray[50],
      },
      divider: gray[80],
      navigation: {
        background: black,
        indicator: blue[40],
        color: gray[30],
        selectedColor: white,
        navItem: {
          hoverBackground: gray[90],
        },
      },
      // Required by @backstage-community/plugin-cost-insights, which extends
      // the palette with a `tooltip` key. Without this the plugin falls back
      // to undefined and renders an unreadable tooltip.
      tooltip: {
        background: gray[10],
        color: gray[100],
      },
    } as any,
  }),

  defaultPageTheme: 'home',
  fontFamily: fontFamilies.sans,

  // Use theme shape instead of CSS
  shape: {
    borderRadius: 0, // Carbon sharp corners
  },

  // Default props for components
  defaultComponentProps: {
    MuiTypography: {
      color: 'textPrimary',
    },
  },

  components: {
    // Global CSS baseline
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          color: gray[10],
        },
        main: {
          color: gray[10],
        },
      },
    },

    BackstageHeader: {
      styleOverrides: {
        header: ({ theme }: any) => ({
          backgroundImage: 'unset',
          backgroundColor: theme.palette.navigation.background,
          color: theme.palette.text.primary,
          borderBottom: `4px solid ${theme.palette.primary.main}`,
          // Ensure all header content is visible
          '& *': {
            color: 'inherit',
          },
          '& svg': {
            color: theme.palette.text.primary,
            fill: 'currentColor',
          },
          '& button': {
            color: theme.palette.text.primary,
          },
          '& a': {
            color: theme.palette.text.primary,
          },
          // Icon buttons specifically
          '& .MuiIconButton-root': {
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor:
                theme.palette.navigation.navItem?.hoverBackground,
            },
          },
          // Subtitles and headings in header
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            color: theme.palette.text.primary,
          },
          // Typography variants
          '& .MuiTypography-subtitle1, & .MuiTypography-subtitle2': {
            color: theme.palette.text.secondary,
          },
        }),
        title: ({ theme }: any) => ({
          color: theme.palette.text.primary,
        }),
        subtitle: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
        }),
        // Upstream `type` (entity-kind label, e.g. "component — github:github-repo")
        // and `breadcrumb` (TechDocs path) both color from `theme.page.fontColor`,
        // which defaults to white for the legacy dark Backstage header. Our header
        // background is set per-theme, so route both through palette.text.secondary
        // for consistent contrast.
        type: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
        }),
        breadcrumb: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
        }),
      },
    },

    BackstageSidebar: {
      styleOverrides: {
        drawer: ({ theme }: any) => ({
          backgroundColor: theme.palette.navigation.background,
          borderRight: `1px solid ${theme.palette.divider}`,
          // Ensure sidebar navigation text is visible
          '& .MuiListItem-root': {
            color: theme.palette.navigation.color,
            '&.Mui-selected': {
              color: theme.palette.navigation.selectedColor,
              backgroundColor:
                theme.palette.navigation.navItem?.hoverBackground,
            },
            '&:hover': {
              backgroundColor:
                theme.palette.navigation.navItem?.hoverBackground,
            },
          },
          '& .MuiListItemText-primary': {
            color: 'inherit',
          },
          '& .MuiListItemIcon-root': {
            color: 'inherit',
          },
        }),
      },
    },

    // Backstage InfoCard and EntityLayout components
    BackstageInfoCard: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          '& *': {
            color: 'inherit',
          },
        }),
      },
    },

    BackstageHeaderLabel: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
        }),
        label: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
        }),
        value: ({ theme }: any) => ({
          color: theme.palette.text.primary,
        }),
      },
    },

    MuiCard: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          '&:hover': {
            borderColor: theme.palette.primary.main,
          },
        }),
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
        }),
        elevation1: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
        },
        elevation2: {
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: fontFamilies.sans,
          fontWeight: 400,
          letterSpacing: body01.letterSpacing,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: blue[30],
          },
          '&:active': {
            backgroundColor: blue[50],
          },
        },
        containedSecondary: {
          '&:hover': {
            backgroundColor: purple[40],
          },
        },
        outlined: ({ theme }: any) => ({
          borderColor: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: `${theme.palette.primary.main}1a`, // 10% opacity
            borderColor: theme.palette.primary.light,
          },
        }),
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          fontFamily: fontFamilies.sans,
        }),
        head: {
          backgroundColor: gray[80],
          fontWeight: 600,
          letterSpacing: body01.letterSpacing,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16, // Exception to zero border-radius
          fontFamily: fontFamilies.sans,
        },
        filled: {
          backgroundColor: gray[80],
        },
        outlined: {
          borderColor: gray[60],
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          textTransform: 'none',
          fontFamily: fontFamilies.sans,
          letterSpacing: body01.letterSpacing,
          color: theme.palette.text.secondary,
          '&.Mui-selected': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }: any) => ({
          backgroundColor: theme.palette.primary.main,
          height: 3,
        }),
      },
    },

    // Input components
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.text.secondary,
          },
        }),
      },
    },

    // Icon buttons - ensure visibility
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor:
              theme.palette.navigation.navItem?.hoverBackground || gray[90],
          },
        }),
      },
    },

    // SVG Icons - ensure visibility
    MuiSvgIcon: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          color: theme.palette.text.primary,
        }),
      },
    },

    // Typography - ensure all text is readable with proper defaults
    MuiTypography: {
      defaultProps: {
        color: 'textPrimary',
      },
      styleOverrides: {
        root: {
          // Let color inherit from variant or parent

          // Inside the cost-insights chart tooltip (background is light in
          // dark mode), text must inherit from the tooltip container so it
          // stays readable. The `makeStyles-tooltip-*` class comes from
          // @backstage-community/plugin-cost-insights useTooltipStyles().
          '[class*="makeStyles-tooltip-"] &': {
            color: 'inherit',
          },
        },
        h1: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          fontWeight: 600,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        h2: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          fontWeight: 600,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        h3: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          fontWeight: 600,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        h4: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          fontWeight: 600,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        h5: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          fontWeight: 600,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        h6: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          fontWeight: 600,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        body1: {
          // Inherit from root
        },
        body2: {
          // Inherit from root
        },
        subtitle1: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        subtitle2: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        caption: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
      },
    },

    // Focus states
    MuiFocusVisible: {
      styleOverrides: {
        root: {
          outline: `2px solid ${blue[40]}`,
          outlineOffset: '2px',
        },
      },
    },
  },
} as UnifiedThemeOptions);
